/* eslint-disable no-restricted-imports */

import {
  TabContext,
  TabContextProps,
  TabList,
  TabListProps,
  TabPanel,
  TabPanelProps,
} from '@mui/lab';
import {
  Tab as MuiTab,
  Tabs as MuiTabs,
  TabProps,
  TabsProps,
} from '@mui/material';
import { tabClasses } from '@mui/material/Tab';
import { tabsClasses } from '@mui/material/Tabs';

import mixinSx from '@watershed/style/mixinSx';

import Tooltip, { type TooltipProps } from './Tooltip';
// Because `Tab` and `Tabs` are almost-always imported together, and `TabPanel`
// and `TabContext` live in mui/labs, let's export 'em from here side-by-side so
// it's wild-easy to import them all together.
export { TabPanel, TabContext, TabList, tabClasses, tabsClasses };
export type {
  TabsProps,
  TabProps,
  TabPanelProps,
  TabContextProps,
  TabListProps,
};

/**
 * Customizes MUI Tabs to match Watershed's design system:
 * - Uses a rounded indicator line under the selected tab
 * - Uses our cobalt brand color for the indicator
 */
export const Tabs = ({ sx, ...props }: TabsProps) => (
  <MuiTabs
    {...props}
    sx={mixinSx(
      (theme) => ({
        [`& .${tabsClasses.indicator}`]: {
          borderRadius: '100px',
          backgroundColor: theme.palette.cobalt,
        },
      }),
      sx
    )}
  />
);

/**
 * Customizes MUI Tab to match Watershed's design system:
 * - Uses semi-bold font weight
 * - Uses secondary text color for unselected tabs
 * - Uses cobalt brand color for selected tab
 */
const WatershedTab = ({ sx, ...props }: TabProps) => (
  <MuiTab
    {...props}
    sx={mixinSx(
      {
        fontWeight: 600,
        color: (theme) => theme.palette.text.secondary,
        [`&.${tabClasses.selected}`]: {
          color: (theme) => theme.palette.cobalt,
        },
      },
      sx
    )}
  />
);

/**
 * Wraps a MUI Tab in a MUI Tooltip, so you can show a tooltip on-hover.
 *
 * (For example: when a tab is disabled, it's often nice to have a tooltip to
 * explain *why* it's disabled.) */
export function Tab({
  tooltip,
  tooltipProps,
  href,
  ...rest
}: {
  tooltip?: TooltipProps['title'];
  tooltipProps?: Omit<TooltipProps, 'title' | 'children'>;
  href?: string;
} & TabProps) {
  // NOTE (bryan): I don't know why this isn't in TabProps, but it's a valid
  // prop for the MuiTab component.
  const tabProps = { ...rest, href };

  // If there aren't any tooltip props, just render the tab.
  if (!tooltip && !tooltipProps) {
    return <WatershedTab {...tabProps} />;
  }

  // We wrap the <Tab> in a <div> so the Tooltip can properly capture hover
  // events. MuiTab doesn't reliably forward these events to parent components.
  return (
    <Tooltip title={tooltip} {...tooltipProps}>
      <div>
        <WatershedTab {...tabProps} />
      </div>
    </Tooltip>
  );
}
